import { useCallback, useState } from 'react';
import { useRegistration } from '@/context/RegistrationContext';
import { cmsApiClient, CmsApiError } from '@/services/cmsApiClient';
import { useAnalytics } from '@/hooks/useAnalytics';
import { ERROR_API_CALL } from '@/consts/events';
import { toIsoBirthDate } from '@/utils/date';
import type { MedicareCoverageData, TrackType } from '@/types/registration';

export type EligibilitySubmissionOutcome = {
  /**
   * Route to navigate to once submission settles:
   * - '/eligibility-result' when at least one track was submitted to CMS
   * - '' when every submission failed (caller should surface a retry prompt
   *   instead of navigating to a result screen that has nothing to poll)
   */
  route: string;
  candidateCount: number;
  submittedCount: number;
  failedCount: number;
};

/** The demographics needed for a CMS $check-eligibility call. Passed explicitly
 *  by MedicareCoverageStep (fresh Formik values) so the up-front call does not
 *  read a not-yet-committed context on the same tick; falls back to the persisted
 *  medicareCoverage otherwise. */
export type EligibilityPatient = Pick<
  MedicareCoverageData,
  'mbi' | 'firstName' | 'lastName' | 'dateOfBirth' | 'gender'
>;

/** Thrown when the patient's DOB is missing/invalid — unrecoverable from the
 *  screening flow (the caller routes away rather than offering a retry). */
export const MISSING_DOB_ERROR = 'ELIGIBILITY_MISSING_DOB';

/** Thrown when gender is null — can happen for sessions restored from storage
 *  written before the gender field existed. Unrecoverable without re-entering
 *  the Medicare form. */
export const MISSING_GENDER_ERROR = 'ELIGIBILITY_MISSING_GENDER';

const RESULT_ROUTE = '/eligibility-result';

// PRD 01 v0.7 §6.2 (RND-36523): eligibility is checked for ALL candidate tracks
// up front, before the health-conditions questionnaire — not derived from the
// patient's self-reported conditions. MSK is out of web-reg scope.
const UP_FRONT_TRACKS: TrackType[] = ['eCKM', 'CKM', 'BH'];

/**
 * RND-33880 / RND-36523 — up-front eligibility trigger.
 *
 * Fires one `POST /eligibility` per candidate track (eCKM, CKM, BH) to the CMS
 * Integration Service — no batching, CMS requires a separate `$check-eligibility`
 * per track. Called right after personal info (MedicareCoverageStep) so the CMS
 * result can drive which condition screens and screeners are shown. Each
 * submission is stored as a per-track `EligibilityTrackResult` so
 * EligibilityResultStep (RND-33881) can poll `GET /eligibility/:submissionId`.
 *
 * This hook no longer computes or writes the final `eligibility` — the candidate
 * set is a fixed 3 tracks, and the final track assignment is computed after the
 * questionnaire by `useEligibilityFinalize` (CMS-eligible ∩ condition-confirmed).
 */
export const useEligibilitySubmission = () => {
  const { registrationData, upsertEligibilityResult } = useRegistration();
  const { trackError } = useAnalytics();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const submit = useCallback(
    async (patient?: EligibilityPatient): Promise<EligibilitySubmissionOutcome> => {
      // Prefer the caller-supplied demographics (fresh Formik values) over the
      // persisted context, which may not have flushed yet on the same tick.
      const src = patient ?? registrationData.medicareCoverage;
      const { firstName, lastName, dateOfBirth, mbi, gender } = src;
      const patientName = { family: lastName, given: [firstName] };
      const birthDate = toIsoBirthDate(dateOfBirth);
      if (!birthDate) {
        trackError({
          event: ERROR_API_CALL,
          properties: { service: 'cms_eligibility', reason: 'MISSING_DOB' },
        });
        throw new Error(MISSING_DOB_ERROR);
      }

      if (!gender) {
        trackError({
          event: ERROR_API_CALL,
          properties: { service: 'cms_eligibility', reason: 'MISSING_GENDER' },
        });
        throw new Error(MISSING_GENDER_ERROR);
      }

      const candidateTracks = UP_FRONT_TRACKS;

      setIsSubmitting(true);
      try {
        // One submission per candidate track, fired in parallel. allSettled so a
        // single failing track does not abort the others.
        const outcomes = await Promise.allSettled(
          candidateTracks.map((track) =>
            cmsApiClient.postEligibility({
              mbi,
              track,
              patientName,
              gender,
              birthDate,
            }),
          ),
        );

        let submittedCount = 0;
        let failedCount = 0;
        const failures: Array<{ track: TrackType; status?: number }> = [];
        outcomes.forEach((outcome, index) => {
          const track = candidateTracks[index];
          if (outcome.status === 'fulfilled') {
            submittedCount += 1;
            upsertEligibilityResult({
              track,
              submissionId: outcome.value.submissionId,
              status: 'processing',
            });
          } else {
            failedCount += 1;
            const reason = outcome.reason;
            failures.push({ track, status: reason instanceof CmsApiError ? reason.status : undefined });
            upsertEligibilityResult({ track, status: 'error' });
          }
        });

        // Surface CMS submission failures for ops (no PHI: only track names,
        // HTTP status, and counts — never the request payload).
        if (failures.length > 0) {
          trackError({
            event: ERROR_API_CALL,
            properties: {
              service: 'cms_eligibility',
              failedTracks: failures.map((f) => f.track),
              // Sentinel for non-HTTP failures (network/timeout/plain Error) so the
              // ops dashboard can group them distinctly from a real status code.
              statuses: failures.map((f) => f.status ?? 'unknown'),
              candidateCount: candidateTracks.length,
            },
          });
        }

        return {
          route: submittedCount > 0 ? RESULT_ROUTE : '',
          candidateCount: candidateTracks.length,
          submittedCount,
          failedCount,
        };
      } finally {
        setIsSubmitting(false);
      }
      // Scope deps to the fields actually read. `registrationData` is a fresh
      // object on every context setter (including the upsertEligibilityResult
      // calls fired inside this loop), so depending on the whole object would
      // re-create `submit` mid-flight.
    },
    [registrationData.medicareCoverage, upsertEligibilityResult, trackError],
  );

  return { submit, isSubmitting };
};
